<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="close">
    <div class="modal-content-outer" @click.stop>
      <div
        class="modal-content bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]"
      >
        <!-- Modal Header (Apple-Style Light Theme) -->
        <div
          class="modal-header bg-gradient-to-r from-white via-sky-50/30 to-white p-3 border-b border-sky-100 flex items-center justify-between flex-shrink-0"
        >
          <div class="flex items-center gap-2.5">
            <div
              class="min-w-7 min-h-7 rounded-xl bg-[#eef6fa] text-[#2b79a8] flex items-center justify-center text-xs shadow-xs border border-sky-200/60"
            >
              <i class="fa-solid fa-dice"></i>
            </div>
            <div>
              <h2
                class="text-sm font-bold text-slate-800 m-0 leading-tight tracking-tight"
              >
                {{ title }}
              </h2>
            </div>
          </div>
          <button
            @click="close"
            class="w-6 h-6 rounded-full bg-slate-100 hover:bg-[#eef6fa] text-slate-500 hover:text-[#2b79a8] flex items-center justify-center transition-all cursor-pointer border border-slate-200 hover:border-sky-200"
            title="Close"
          >
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>

        <!-- Scrollable Modal Body -->
        <div
          v-if="roundGeneralInfo && !isFirstLoading"
          id="round-info-container"
          class="overflow-y-auto p-3 flex flex-col gap-2.5 flex-grow"
        >
          <!-- Compact Round Summary Bar (Apple-style pill & card) -->
          <div
            class="bg-white px-3.5 py-1.5 rounded-xl border border-sky-100 shadow-2xs flex items-center justify-between flex-wrap gap-2 text-xs"
          >
            <!-- Round ID -->
            <div class="flex items-center gap-1.5 min-w-0">
              <span
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >Round ID:</span
              >
              <span
                class="text-xs font-bold text-slate-800 truncate"
                :title="roundGeneralInfo.roundId"
              >
                {{ roundGeneralInfo.roundId || "-" }}
              </span>
            </div>

            <!-- Extracted Number -->
            <div class="flex items-center gap-1.5">
              <span
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >Extracted:</span
              >
              <div
                class="flex items-center gap-1"
                v-if="
                  roundGeneralInfo.multiplier !== undefined &&
                  roundGeneralInfo.multiplier !== null &&
                  roundGeneralInfo.multiplier !== '' &&
                  Number(roundGeneralInfo.multiplier) !== 999
                "
              >
                <span
                  :class="[
                    'roulette-chip chip-table !w-4.5 !h-4.5 !text-[9px]',
                    getChipColorClass(roundGeneralInfo.multiplier),
                  ]"
                >
                  <span class="chip-number">{{
                    roundGeneralInfo.multiplier
                  }}</span>
                </span>
                <span class="text-xs font-semibold text-slate-600">
                  {{ getChipDescriptor(roundGeneralInfo.multiplier) }}
                </span>
              </div>
              <span v-else class="text-xs font-medium text-slate-400">-</span>
            </div>

            <!-- Start Time -->
            <div class="flex items-center gap-1">
              <i class="fa-solid fa-clock text-slate-400 text-[10px]"></i>
              <span
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >Start:</span
              >
              <span class="text-xs font-medium text-slate-700">
                {{ formatDate(roundGeneralInfo.created_at) }}
              </span>
            </div>

            <!-- End Time -->
            <div class="flex items-center gap-1">
              <i
                class="fa-solid fa-calendar-check text-slate-400 text-[10px]"
              ></i>
              <span
                class="text-[10px] font-bold text-slate-400 uppercase tracking-wider"
                >End:</span
              >
              <span class="text-xs font-medium text-slate-700">
                {{
                  formatDate(
                    roundGeneralInfo.updated_at || roundGeneralInfo.end_at,
                  )
                }}
              </span>
            </div>
          </div>

          <!-- Ultra-Compact Selected Single Spin Detail Card -->
          <div
            class="w-full animation-slide"
            v-if="isValidInfo && selectedPlayerInRoundInfo"
          >
            <div
              class="bg-white rounded-2xl border border-sky-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] overflow-hidden transition-all"
            >
              <!-- Ultra-Compact Header banner (Accordion Header with Brand Azzurrino Gradient) -->
              <div
                @click="isSpinDetailCollapsed = !isSpinDetailCollapsed"
                class="bg-gradient-to-r from-[#2b79a8] via-[#246e99] to-[#1c5a80] px-3.5 py-1.5 text-white flex items-center justify-between flex-wrap gap-1.5 cursor-pointer select-none"
                :title="
                  isSpinDetailCollapsed
                    ? 'Click to expand'
                    : 'Click to collapse'
                "
              >
                <div class="flex items-center gap-2 flex-wrap">
                  <i class="fa-solid fa-receipt text-sky-200 text-[11px]"></i>
                  <span class="font-bold text-xs tracking-wide select-all">
                    {{ selectedPlayerInRoundInfo.uuid }}
                  </span>

                  <!-- Is Valid Badge (Prominent / Highlighted) -->
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider shadow-xs flex items-center gap-1 border"
                    :class="
                      selectedPlayerInRoundInfo.is_valid === true ||
                      selectedPlayerInRoundInfo.is_valid === 1 ||
                      selectedPlayerInRoundInfo.is_valid === 'true'
                        ? 'bg-emerald-500 text-white border-emerald-300 ring-1 ring-emerald-300/50'
                        : 'bg-rose-500 text-white border-rose-300 ring-1 ring-rose-300/50'
                    "
                  >
                    <i
                      :class="
                        selectedPlayerInRoundInfo.is_valid === true ||
                        selectedPlayerInRoundInfo.is_valid === 1 ||
                        selectedPlayerInRoundInfo.is_valid === 'true'
                          ? 'fa-solid fa-circle-check text-[9px]'
                          : 'fa-solid fa-circle-xmark text-[9px]'
                      "
                    ></i>
                    {{
                      selectedPlayerInRoundInfo.is_valid === true ||
                      selectedPlayerInRoundInfo.is_valid === 1 ||
                      selectedPlayerInRoundInfo.is_valid === "true"
                        ? "Valid"
                        : "Invalid"
                    }}
                  </span>

                  <!-- Is Closed Badge -->
                  <span
                    class="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider border"
                    :class="
                      selectedPlayerInRoundInfo.is_closed === true ||
                      selectedPlayerInRoundInfo.is_closed === 1 ||
                      selectedPlayerInRoundInfo.is_closed === 'true'
                        ? 'bg-slate-900/40 text-slate-100 border-white/20'
                        : 'bg-sky-400/20 text-sky-100 border-white/10'
                    "
                  >
                    <i
                      class="fa-solid text-[7px] mr-0.5"
                      :class="
                        selectedPlayerInRoundInfo.is_closed
                          ? 'fa-lock'
                          : 'fa-lock-open'
                      "
                    ></i>
                    {{
                      selectedPlayerInRoundInfo.is_closed ? "Closed" : "Open"
                    }}
                  </span>

                  <span
                    v-if="selectedPlayerInRoundInfo.is_manual_closed"
                    class="px-2.5 py-0.5 rounded-full text-[9px] font-extrabold bg-amber-500 text-white uppercase tracking-wider shadow-xs flex items-center gap-1 border border-amber-300"
                    title="Manually Closed"
                  >
                    <i class="fa-solid fa-hand text-[8px]"></i>
                    Manual Closed
                  </span>

                  <span
                    v-if="isSpinDetailCollapsed"
                    class="text-[10px] text-sky-200 font-semibold italic ml-1"
                  >
                    (Collapsed • Click to expand)
                  </span>
                </div>
                <div class="flex items-center gap-1.5" @click.stop>
                  <!-- Accordion Toggle Button -->
                  <button
                    @click="isSpinDetailCollapsed = !isSpinDetailCollapsed"
                    class="w-6 h-6 rounded-full hover:bg-white/20 text-sky-100 hover:text-white flex items-center justify-center transition-all cursor-pointer border-none"
                    :title="
                      isSpinDetailCollapsed
                        ? 'Expand Bet Details'
                        : 'Collapse Bet Details'
                    "
                  >
                    <i
                      class="fa-solid text-xs transition-transform duration-200"
                      :class="
                        isSpinDetailCollapsed
                          ? 'fa-chevron-down'
                          : 'fa-chevron-up'
                      "
                    ></i>
                  </button>
                  <!-- Dismiss Button -->
                  <button
                    @click="selectedPlayerInRoundInfo = null"
                    class="w-5 h-5 rounded-full hover:bg-white/20 text-sky-100 hover:text-white flex items-center justify-center transition-all cursor-pointer border-none ml-0.5"
                    title="Close"
                  >
                    <i class="fa-solid fa-xmark text-xs"></i>
                  </button>
                </div>
              </div>

              <!-- Accordion Body (Collapsible Grid & Placed Bets) -->
              <div v-show="!isSpinDetailCollapsed" class="accordion-content">
                <!-- Ultra-Compact Spin Details Grid (6-Column Dense Layout) -->
                <div
                  class="p-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 bg-slate-50/50"
                >
                  <!-- Player ID -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-user text-blue-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Player ID
                      </div>
                      <div
                        @click="openPlayerDetail(selectedPlayerInRoundInfo)"
                        class="text-[11px] font-bold text-blue-600 truncate hover:underline cursor-pointer leading-tight mt-0.5"
                      >
                        {{ selectedPlayerInRoundInfo.user_id || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Bet Amount -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-coins text-amber-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Bet Amount
                      </div>
                      <div
                        class="text-[11px] font-bold text-slate-800 truncate leading-tight mt-0.5"
                      >
                        {{
                          selectedPlayerInRoundInfo.amount !== undefined &&
                          selectedPlayerInRoundInfo.amount !== null
                            ? parseFloat(
                                selectedPlayerInRoundInfo.amount,
                              ).toFixed(2)
                            : "0.00"
                        }}
                        <span class="text-[10px] text-slate-500">{{
                          selectedPlayerInRoundInfo.currency_code
                        }}</span>
                        <span
                          class="text-slate-400 font-normal text-[9px]"
                          v-if="
                            selectedPlayerInRoundInfo.amount_converted !==
                            undefined
                          "
                        >
                          /{{
                            parseFloat(
                              selectedPlayerInRoundInfo.amount_converted,
                            ).toFixed(2)
                          }}€
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Win -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-trophy text-emerald-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Win
                      </div>
                      <div
                        class="text-[11px] font-bold truncate leading-tight mt-0.5"
                        :class="
                          parseFloat(selectedPlayerInRoundInfo.win) > 0
                            ? 'text-emerald-600'
                            : 'text-slate-700'
                        "
                      >
                        {{
                          selectedPlayerInRoundInfo.win !== undefined &&
                          selectedPlayerInRoundInfo.win !== null
                            ? parseFloat(selectedPlayerInRoundInfo.win).toFixed(
                                2,
                              )
                            : "0.00"
                        }}
                        <span class="text-[10px] text-slate-500">{{
                          selectedPlayerInRoundInfo.currency_code
                        }}</span>
                        <span
                          class="text-slate-400 font-normal text-[9px]"
                          v-if="
                            selectedPlayerInRoundInfo.win_converted !==
                            undefined
                          "
                        >
                          /{{
                            parseFloat(
                              selectedPlayerInRoundInfo.win_converted,
                            ).toFixed(2)
                          }}€
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Balance -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-wallet text-teal-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Balance
                      </div>
                      <div
                        class="text-[11px] font-bold text-slate-800 truncate leading-tight mt-0.5"
                      >
                        {{
                          selectedPlayerInRoundInfo.balance !== undefined &&
                          selectedPlayerInRoundInfo.balance !== null
                            ? parseFloat(
                                selectedPlayerInRoundInfo.balance,
                              ).toFixed(2)
                            : "-"
                        }}
                        <span class="text-[10px] text-slate-500">{{
                          selectedPlayerInRoundInfo.currency_code
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Game ID -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-gamepad text-purple-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Game ID
                      </div>
                      <div
                        class="text-[11px] font-semibold text-slate-800 truncate leading-tight mt-0.5"
                      >
                        {{ selectedPlayerInRoundInfo.game_id || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Table -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-dice-d6 text-slate-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Table
                      </div>
                      <div
                        class="text-[11px] font-semibold text-slate-800 truncate leading-tight mt-0.5"
                      >
                        {{ selectedPlayerInRoundInfo.table || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Extracted Number -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-circle-dot text-rose-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Extracted
                      </div>
                      <div
                        class="flex items-center gap-1 mt-0.5"
                        v-if="
                          selectedPlayerInRoundInfo.multiplier !== undefined &&
                          selectedPlayerInRoundInfo.multiplier !== null &&
                          selectedPlayerInRoundInfo.multiplier !== '' &&
                          Number(selectedPlayerInRoundInfo.multiplier) !== 999
                        "
                      >
                        <span
                          :class="[
                            'roulette-chip chip-table !w-4 !h-4 !text-[8px]',
                            getChipColorClass(
                              selectedPlayerInRoundInfo.multiplier,
                            ),
                          ]"
                        >
                          <span class="chip-number">{{
                            selectedPlayerInRoundInfo.multiplier
                          }}</span>
                        </span>
                        <span class="text-[10px] font-semibold text-slate-600">
                          {{
                            getChipDescriptor(
                              selectedPlayerInRoundInfo.multiplier,
                            )
                          }}
                        </span>
                      </div>
                      <div
                        v-else
                        class="text-[11px] font-medium text-slate-400"
                      >
                        -
                      </div>
                    </div>
                  </div>

                  <!-- Skin ID -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-layer-group text-amber-600 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Skin ID
                      </div>
                      <div
                        class="text-[11px] font-semibold text-slate-800 truncate leading-tight mt-0.5"
                      >
                        {{ selectedPlayerInRoundInfo.skin_id || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Token -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-key text-indigo-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Token
                      </div>
                      <div
                        class="text-[10px] font-medium text-slate-700 truncate leading-tight mt-0.5"
                        :title="selectedPlayerInRoundInfo.token"
                      >
                        {{ selectedPlayerInRoundInfo.token || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Connection ID -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-network-wired text-cyan-500 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Connection ID
                      </div>
                      <div
                        class="text-[10px] font-medium text-slate-700 truncate leading-tight mt-0.5"
                        :title="selectedPlayerInRoundInfo.connection_id"
                      >
                        {{ selectedPlayerInRoundInfo.connection_id || "-" }}
                      </div>
                    </div>
                  </div>

                  <!-- Start Time -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-clock text-slate-400 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Start Time
                      </div>
                      <div
                        class="text-[10px] font-medium text-slate-600 truncate leading-tight mt-0.5"
                      >
                        {{ formatDate(selectedPlayerInRoundInfo.created_at) }}
                      </div>
                    </div>
                  </div>

                  <!-- Updated At -->
                  <div
                    class="flex items-center gap-1.5 p-1.5 bg-white rounded-xl border border-sky-100/80 shadow-2xs hover:border-sky-200 transition-all min-w-0"
                  >
                    <i
                      class="fa-solid fa-calendar-check text-slate-400 text-[11px] w-3 text-center flex-shrink-0"
                    ></i>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-bold text-slate-400 uppercase tracking-wider leading-none"
                      >
                        Updated At
                      </div>
                      <div
                        class="text-[10px] font-medium text-slate-600 truncate leading-tight mt-0.5"
                      >
                        {{ formatDate(selectedPlayerInRoundInfo.updated_at) }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="selectedPlayerInRoundInfo.is_manual_closed"
                    class="flex items-center gap-1.5 p-1.5 bg-gradient-to-br from-amber-50 to-orange-50/80 rounded-xl border border-amber-300 ring-1 ring-amber-400/40 shadow-2xs hover:border-amber-400 transition-all min-w-0"
                  >
                    <div
                      class="w-5 h-5 rounded-lg bg-amber-500 text-white flex items-center justify-center text-[10px] flex-shrink-0 shadow-2xs"
                    >
                      <i class="fa-solid fa-hand"></i>
                    </div>
                    <div class="min-w-0">
                      <div
                        class="text-[8px] font-black text-amber-700 uppercase tracking-wider leading-none"
                      >
                        Manual Closed
                      </div>
                      <div
                        class="text-[11px] font-black text-amber-900 truncate leading-tight mt-0.5"
                      >
                        YES
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Interactive Roulette BetBoard Carousel -->
                <div
                  v-if="
                    selectedPlayerInRoundInfo.type &&
                    selectedPlayerInRoundInfo.type.length > 0
                  "
                  class="p-2.5 bg-[#f0f7fb]/40 border-t border-sky-100"
                >
                  <RouletteBetBoardCarousel
                    :bets="selectedPlayerInRoundInfo.type"
                    :currency="selectedPlayerInRoundInfo.currency_code || 'EUR'"
                    :win="selectedPlayerInRoundInfo.win"
                    :win-converted="selectedPlayerInRoundInfo.win_converted"
                    :winning-number="
                      selectedPlayerInRoundInfo.multiplier !== undefined &&
                      selectedPlayerInRoundInfo.multiplier !== null &&
                      selectedPlayerInRoundInfo.multiplier !== '' &&
                      Number(selectedPlayerInRoundInfo.multiplier) !== 999
                        ? Number(selectedPlayerInRoundInfo.multiplier)
                        : roundGeneralInfo?.multiplier !== undefined &&
                          roundGeneralInfo?.multiplier !== null &&
                          roundGeneralInfo?.multiplier !== '' &&
                          Number(roundGeneralInfo.multiplier) !== 999
                        ? Number(roundGeneralInfo.multiplier)
                        : null
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Apple-Style Search & Filter Bar -->
          <div
            class="bg-white p-2 rounded-2xl border border-sky-100 shadow-2xs flex items-center gap-2"
          >
            <div class="relative flex-grow">
              <i
                class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"
              ></i>
              <input
                type="text"
                placeholder="Filter by Player ID in this round..."
                v-model="searchPlayerID"
                @keyup.enter="searchAndResetPagination"
                class="w-full h-8 pl-8 pr-3 bg-slate-50/80 border border-slate-200 rounded-full text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2b79a8]/20 focus:border-[#2b79a8] transition-all"
              />
            </div>

            <button
              @click="searchAndResetPagination"
              class="h-8 px-4 rounded-full bg-[#2b79a8] hover:bg-[#236892] text-white text-xs font-bold flex items-center gap-1 transition-all cursor-pointer shadow-xs border-none"
            >
              Search
            </button>
            <button
              @click="resetFilter"
              class="h-8 px-4 rounded-full bg-slate-100 hover:bg-[#eef6fa] text-slate-700 hover:text-[#2b79a8] text-xs font-bold flex items-center gap-1 transition-all cursor-pointer border border-slate-200 hover:border-sky-200"
            >
              Reset
            </button>
          </div>

          <!-- Bets Table in this Round (Apple Theme with PrimeVue DataTable / Custom Loader) -->
          <div class="w-full">
            <div
              v-if="isLoading"
              class="waiting-loader bg-white rounded-2xl border border-sky-100 shadow-2xs py-10"
            >
              <Loader />
            </div>
            <div
              v-else
              class="table-container-custom shadow-[0_4px_20px_rgba(0,0,0,0.04)] rounded-2xl overflow-hidden border border-sky-100 bg-white"
            >
              <DataTable
                :value="playersInRoundInfo"
                :lazy="true"
                :paginator="true"
                :rows="selectedLimit"
                :totalRecords="totalElements"
                :first="(currentPage - 1) * selectedLimit"
                @page="onPage"
                :rowsPerPageOptions="[15, 25, 50, 100]"
                removableSort
                stripedRows
                scrollable
                responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              >
                <template #empty>
                  <div
                    class="text-center py-5 text-slate-400 font-medium text-xs"
                  >
                    No bets found in this round.
                  </div>
                </template>

                <!-- Player ID -->
                <Column
                  field="user_id"
                  header="Player ID"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span
                      @click="openPlayerDetail(slotProps.data)"
                      class="text-blue-600 font-bold hover:underline decoration-2 underline-offset-4 cursor-pointer"
                    >
                      {{ slotProps.data.user_id || "-" }}
                    </span>
                  </template>
                </Column>

                <!-- Round ID -->
                <Column
                  field="roundId"
                  header="Round ID"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span
                      class="text-xs font-medium text-slate-700"
                      :title="slotProps.data.roundId"
                    >
                      {{ slotProps.data.roundId || "-" }}
                    </span>
                  </template>
                </Column>

                <!-- Game ID -->
                <Column
                  field="game_id"
                  header="Game ID"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span class="text-xs font-medium text-slate-700">
                      {{ slotProps.data.game_id || "-" }}
                    </span>
                  </template>
                </Column>

                <!-- Multiplier / Extracted Number -->
                <Column
                  field="multiplier"
                  header="Extracted Number"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <div
                      v-if="
                        slotProps.data.multiplier !== undefined &&
                        slotProps.data.multiplier !== null &&
                        slotProps.data.multiplier !== '' &&
                        Number(slotProps.data.multiplier) !== 999
                      "
                      class="flex items-center gap-1.5"
                    >
                      <span
                        :class="[
                          'roulette-chip chip-table !w-5 !h-5 !text-[9px]',
                          getChipColorClass(slotProps.data.multiplier),
                        ]"
                      >
                        <span class="chip-number">{{
                          slotProps.data.multiplier
                        }}</span>
                      </span>
                      <span class="text-xs font-semibold text-slate-500">
                        {{ getChipDescriptor(slotProps.data.multiplier) }}
                      </span>
                    </div>
                    <span v-else class="text-xs font-medium text-slate-400"
                      >-</span
                    >
                  </template>
                </Column>

                <!-- Amount / Amount Converted -->
                <Column
                  field="amount"
                  header="Amount"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span class="text-xs font-semibold text-slate-800">
                      {{
                        slotProps.data.amount !== undefined &&
                        slotProps.data.amount !== null
                          ? parseFloat(slotProps.data.amount).toFixed(2) +
                            ` ${slotProps.data.currency_code || ""}`
                          : "-"
                      }}
                      <span
                        v-if="
                          slotProps.data.amount_converted !== undefined &&
                          slotProps.data.amount_converted !== null
                        "
                        class="text-slate-400 font-normal text-[11px] ml-1"
                        title="Amount Converted"
                      >
                        /
                        {{
                          parseFloat(slotProps.data.amount_converted).toFixed(2)
                        }}
                        EUR
                      </span>
                    </span>
                  </template>
                </Column>

                <!-- Win / Win Converted -->
                <Column
                  field="win"
                  header="Win"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span
                      :class="[
                        'text-xs font-bold',
                        parseFloat(slotProps.data.win) > 0
                          ? 'text-emerald-600'
                          : 'text-slate-600',
                      ]"
                    >
                      {{
                        slotProps.data.win !== undefined &&
                        slotProps.data.win !== null
                          ? parseFloat(slotProps.data.win).toFixed(2) +
                            ` ${slotProps.data.currency_code || ""}`
                          : "-"
                      }}
                      <span
                        v-if="
                          slotProps.data.win_converted !== undefined &&
                          slotProps.data.win_converted !== null
                        "
                        class="text-slate-400 font-normal text-[11px] ml-1"
                        title="Win Converted"
                      >
                        /
                        {{
                          parseFloat(slotProps.data.win_converted).toFixed(2)
                        }}
                        EUR
                      </span>
                    </span>
                  </template>
                </Column>

                <!-- Status (Is Valid, Is Closed, Is Manual Closed) -->
                <Column
                  header="Status"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <!-- Is Valid Highlighted Badge -->
                      <span
                        class="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border shadow-2xs"
                        :class="
                          slotProps.data.is_valid === true ||
                          slotProps.data.is_valid === 1 ||
                          slotProps.data.is_valid === 'true'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300 ring-1 ring-emerald-300/40'
                            : 'bg-rose-100 text-rose-800 border-rose-300 ring-1 ring-rose-300/40'
                        "
                      >
                        <i
                          :class="
                            slotProps.data.is_valid === true ||
                            slotProps.data.is_valid === 1 ||
                            slotProps.data.is_valid === 'true'
                              ? 'fa-solid fa-circle-check text-[8px] mr-0.5'
                              : 'fa-solid fa-circle-xmark text-[8px] mr-0.5'
                          "
                        ></i>
                        {{
                          slotProps.data.is_valid === true ||
                          slotProps.data.is_valid === 1 ||
                          slotProps.data.is_valid === "true"
                            ? "Valid"
                            : "Not valid"
                        }}
                      </span>

                      <span
                        v-if="slotProps.data.is_manual_closed"
                        class="px-2 py-0.5 rounded-full text-[9px] font-black bg-amber-100 text-amber-900 border border-amber-300 uppercase shadow-2xs"
                        title="Manually Closed"
                      >
                        <i
                          class="fa-solid fa-hand text-[8px] mr-0.5 text-amber-600"
                        ></i>
                        Manual Closed
                      </span>
                    </div>
                  </template>
                </Column>

                <!-- Table -->
                <Column
                  field="table"
                  header="Table"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span class="text-xs font-medium text-slate-700">
                      {{ slotProps.data.table || "-" }}
                    </span>
                  </template>
                </Column>

                <!-- Bet Types Count -->
                <Column
                  field="type"
                  header="Bet Types"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span
                      class="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1"
                      :title="`${slotProps.data.type?.length || 0} bet type(s)`"
                    >
                      <i
                        class="fa-solid fa-layer-group text-slate-400 text-[9px]"
                      ></i>
                      {{ slotProps.data.type?.length || 0 }}
                    </span>
                  </template>
                </Column>

                <!-- Start Time -->
                <Column
                  field="created_at"
                  header="Start Time"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span class="text-[11px] font-medium text-gray-500">
                      {{ formatDate(slotProps.data.created_at) }}
                    </span>
                  </template>
                </Column>

                <!-- Updated At -->
                <Column
                  field="updated_at"
                  header="Updated At"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5"
                  bodyClass="whitespace-nowrap px-3 py-2 text-xs"
                >
                  <template #body="slotProps">
                    <span class="text-[11px] font-medium text-gray-500">
                      {{ formatDate(slotProps.data.updated_at) }}
                    </span>
                  </template>
                </Column>

                <!-- Actions -->
                <Column
                  header="Actions"
                  headerClass="whitespace-nowrap text-[11px] uppercase font-bold text-slate-600 px-3 py-2.5 text-center"
                  bodyClass="whitespace-nowrap px-3 py-2 text-center text-xs"
                >
                  <template #body="slotProps">
                    <div class="flex items-center justify-center gap-1">
                      <button
                        @click="openRoundDetail(slotProps.data)"
                        class="p-1.5 bg-slate-100 hover:bg-emerald-600 hover:text-white text-slate-600 rounded-md transition-all cursor-pointer border-none"
                        title="View Bet Details"
                      >
                        <i class="fa-solid fa-eye text-xs"></i>
                      </button>
                      <button
                        @click="openRoundHistory(slotProps.data)"
                        class="p-1.5 bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-600 rounded-md transition-all cursor-pointer border-none"
                        title="Show History"
                      >
                        <i
                          class="fa-solid fa-arrow-up-right-from-square text-xs"
                        ></i>
                      </button>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>

        <div
          v-else-if="!isFirstLoading && !roundGeneralInfo"
          class="waiting-loader"
        >
          <p class="mb-0 text-slate-500 font-semibold">
            Round detail not available.
          </p>
        </div>
        <div v-else class="waiting-loader">
          <Loader />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { getFetch } from "@/api/axiosClient";
import { mapGetters } from "vuex";
import Loader from "@/slot/components/Loader.vue";
import RouletteBetBoardCarousel from "./RouletteBetBoardCarousel.vue";
import rouletteBetTypes from "../config/rouletteBetTypes.json";

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
const BLACK_NUMBERS = new Set([
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35,
]);

export default {
  name: "LiveCasinoRoundModal",
  components: {
    DataTable,
    Column,
    Loader,
    RouletteBetBoardCarousel,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Live Casino Round Details",
    },
    roundInfo: {
      type: [Object, Array],
      required: false,
      default: () => ({}),
    },
    round_id: {
      type: [String, Number],
      required: false,
    },
    selectedPlayerID: {
      type: String,
      required: false,
    },
    fullInfo: {
      type: Boolean,
      default: false,
    },
    category: {
      type: String,
      default: null,
    },
    skinFilt: {
      required: false,
    },
    closeModal: Function,
  },
  data() {
    return {
      totalElements: 0,
      roundGeneralInfo: null,
      searchPlayerID: "",
      selectedPlayerInRoundInfo: null,
      isSpinDetailCollapsed: false,
      playersInRoundInfo: [],
      isValidInfo: false,
      isLoadingAllPlayerDetail: false,
      selectedLimit: 15,
      isLoading: false,
      isFirstLoading: true,
      currentPage: 1,
      totalPages: 0,
    };
  },
  computed: {
    ...mapGetters(["allowedSkins", "isAdmin", "isMenuOpen", "chatSkinFilter", "skinStorage"]),
    effectiveRoundInfo() {
      if (
        this.roundInfo &&
        typeof this.roundInfo === "object" &&
        Object.keys(this.roundInfo).length > 0
      ) {
        return this.roundInfo;
      }
      if (this.round_id) {
        return {
          roundId: this.round_id,
          round_id: this.round_id,
          uuid: this.round_id,
        };
      }
      return {};
    },
  },
  methods: {
    getBetLabel(type_id) {
      if (!type_id && type_id !== 0) return "-";
      const info = rouletteBetTypes[String(type_id)];
      return info?.label || `#${type_id}`;
    },
    getChipColorClass(val) {
      if (val === undefined || val === null || val === "")
        return "chip-color-neutral";
      const num = Number(val);
      if (num === 0) return "chip-color-green";
      if (RED_NUMBERS.has(num)) return "chip-color-red";
      if (BLACK_NUMBERS.has(num)) return "chip-color-black";
      return "chip-color-neutral";
    },
    getChipDescriptor(val) {
      if (val === undefined || val === null || val === "") return "";
      const num = Number(val);
      if (num === 0) return "Zero (Green)";
      const color = RED_NUMBERS.has(num)
        ? "Red"
        : BLACK_NUMBERS.has(num)
        ? "Black"
        : "";
      const parity = num % 2 === 0 ? "Even" : "Odd";
      return color ? `${color} • ${parity}` : parity;
    },
    checkMultiplier(val) {
      let parsedVal = parseFloat(val);
      if (parsedVal < 1.41) {
        return "low-val";
      } else if (parsedVal > 1.4 && parsedVal < 2.81) {
        return "mid-val";
      } else {
        return "high-val";
      }
    },
    close() {
      if (typeof this.closeModal === "function") {
        this.closeModal();
      }
      this.$emit("close");
    },
    async onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      await this.searchPlayerInRoundID();
    },
    formatDate(dateString, onlyYMD = false) {
      if (!dateString) return "-";

      const date = new Date(dateString);

      if (onlyYMD) {
        const yyyy = date.getUTCFullYear();
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(date.getUTCDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }

      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "UTC",
        hour12: false,
      };

      return date.toLocaleString("it-IT", options);
    },
    async searchRoundIDInfo() {
      const round =
        this.effectiveRoundInfo?.roundId ||
        this.effectiveRoundInfo?.round_id;
      if (!round) {
        this.roundGeneralInfo = this.effectiveRoundInfo;
        return;
      }

      try {
        let provider = process.env.VUE_APP_PROVIDER ?? "theearcasino";
        const data = await getFetch(
          `multipliers/`,
          {
            roundId: round,
          },
          true,
          `${provider}_livecasino`,
        );

        if (data?.data && data.data.length > 0) {
          this.roundGeneralInfo = data.data[0];
        } else {
          this.roundGeneralInfo = this.effectiveRoundInfo;
        }
      } catch (e) {
        console.error("Error fetching round details:", e);
        this.roundGeneralInfo = this.effectiveRoundInfo;
      }
    },
    async searchAndResetPagination() {
      this.currentPage = 1;
      await this.searchPlayerInRoundID();
    },
    async resetFilter() {
      this.searchPlayerID = "";
      this.currentPage = 1;
      this.selectedLimit = 15;
      await this.searchPlayerInRoundID();
    },
    async searchPlayerInRoundID() {
      this.isLoading = true;

      try {
        const roundId =
          this.effectiveRoundInfo?.roundId ||
          this.effectiveRoundInfo?.round_id;
        const betUuid =
          this.effectiveRoundInfo?.uuid ||
          this.effectiveRoundInfo?.bet_id ||
          this.effectiveRoundInfo?.betId;
        let provider = process.env.VUE_APP_PROVIDER ?? "theearcasino";

        const params = {
          page: this.currentPage,
          limit: this.selectedLimit,
          skin_id: this.skinFilt,
        };

        if (roundId) {
          params.roundId = roundId;
        } else if (betUuid) {
          params.uuid = betUuid;
        }

        if (this.searchPlayerID.trim()) {
          params.user_id = this.searchPlayerID.trim();
        }

        const data = await getFetch(
          `spins/roulette/`,
          params,
          true,
          `${provider}_livecasino`,
        );

        this.playersInRoundInfo =
          data?.data || (Array.isArray(data) ? data : []);
        this.totalPages = data?.totalPages || 0;
        this.totalElements =
          data?.totalItems || (Array.isArray(data) ? data.length : 0);
      } catch (error) {
        console.error("Error fetching players in round:", error);
      } finally {
        this.isLoading = false;
        this.scrollContainer();
      }
    },
    async openRoundDetail(row) {
      this.isLoadingAllPlayerDetail = true;

      try {
        let provider = process.env.VUE_APP_PROVIDER ?? "theearcasino";
        const betUuid =
          row?.uuid ||
          row?.bet_id ||
          row?.betId ||
          row?._id ||
          row?.id ||
          row?.round_id ||
          row?.roundId;

        const data = await getFetch(
          `spins/roulette/`,
          {
            uuid: betUuid,
            skin_id: this.skinFilt,
          },
          true,
          `${provider}_livecasino`,
        );

        let betDetail = data;
        if (Array.isArray(data?.data) && data.data.length > 0) {
          betDetail = data.data[0];
        } else if (Array.isArray(data) && data.length > 0) {
          betDetail = data[0];
        } else if (
          data?.data &&
          typeof data.data === "object" &&
          !Array.isArray(data.data)
        ) {
          betDetail = data.data;
        }

        this.selectedPlayerInRoundInfo = betDetail || row;
        this.isSpinDetailCollapsed = false;

        if (
          (!this.roundGeneralInfo || !this.roundGeneralInfo.multiplier) &&
          betDetail?.roundId
        ) {
          const multData = await getFetch(
            `multipliers/`,
            { roundId: betDetail.roundId },
            true,
            `${provider}_livecasino`,
          );
          if (multData?.data && multData.data.length > 0) {
            this.roundGeneralInfo = multData.data[0];
          }
        }
      } catch (error) {
        console.error("Error fetching single spin detail:", error);
        this.selectedPlayerInRoundInfo = row;
      } finally {
        this.isLoadingAllPlayerDetail = false;
        this.isValidInfo = true;
        this.scrollContainer();
      }
    },
    async scrollContainer() {
      const el = document.getElementById("round-info-container");
      if (el) {
        setTimeout(() => {
          el.scrollBy({
            top: -1000,
            behavior: "smooth",
          });
        }, 50);
      }
    },
    async searchSelectedPlayerID() {
      this.isLoadingAllPlayerDetail = true;
      await this.openRoundDetail(this.effectiveRoundInfo);
      this.isLoadingAllPlayerDetail = false;
    },
    openRoundHistory(bet) {
      if (!bet || !bet.roundId || !bet.game_id) return;
      const baseUrl =
        process.env.VUE_APP_HISTORY ||
        "https://stage-casino.theearplatform.com/stagemoregames/historyPage";
      const url = `${baseUrl}/?round_id=${bet.roundId}_${bet.uuid}&game_id=${
        bet.game_id
      }&token=${bet.token || "demo"}&provider_id=207`;
      window.open(url, "_blank");
    },
    async openPlayerDetail(row) {
      const user_id = row?.user_id;
      if (!user_id) return;
      let skin = Number(user_id.split("_")[0]);
      if (!skin) return alert("Player Demo");
      try {
        const data = await getFetch(
          `players/detail/${user_id}?skin_id=${skin}`,
        );

        if (data) {
          const lang = this.$route?.params?.lang || "en";
          const currentSkin =
            this.skinFilt ||
            this.skinStorage ||
            this.$route?.params?.skinFilt ||
            skin;
          const skinPath =
            currentSkin && currentSkin !== "all" ? `/${currentSkin}` : "";
          const category =
            this.category ||
            this.$route?.meta?.section ||
            (this.$route?.path?.includes("/livecasino")
              ? "livecasino"
              : this.$route?.path?.includes("/instant")
              ? "instant"
              : "slot");

          setTimeout(() => {
            window.open(
              `/${lang}${skinPath}/${category}/players/${data._id}`,
              "_blank"
            );
          });
        } else alert("Player detail are not present");
      } catch (error) {
        alert("Player detail are not present");
      }
    },
  },
  async mounted() {
    try {
      document.body.style.overflow = "hidden";

      await this.searchRoundIDInfo();
      await this.searchPlayerInRoundID();

      if (this.fullInfo) {
        await this.searchSelectedPlayerID();
      }
    } catch (error) {
      console.error("Error during initial load:", error);
    } finally {
      this.isFirstLoading = false;
    }
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(0px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 98765432100;
  padding: 1.25rem;
}

.modal-content-outer {
  width: 90%;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
  @media (max-width: 900px) {
    width: 96%;
  }
}

.waiting-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.low-val {
  color: #16a34a;
}
.mid-val {
  color: #ca8a04;
}
.high-val {
  color: #dc2626;
}

/* =========================================
   3D ROULETTE CHIPS IN MODAL TABLE
========================================= */
.roulette-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: inherit;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  user-select: none;
  flex-shrink: 0;
  box-sizing: border-box;
}

.chip-number {
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}

.chip-table {
  width: 24px;
  height: 24px;
  font-size: 10px;
}

.chip-color-red {
  background: radial-gradient(
    circle at 35% 30%,
    #ff5252 0%,
    #e11d48 55%,
    #9f1239 100%
  );
  border: 1.5px solid #fecdd3;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(225, 29, 72, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

.chip-color-black {
  background: radial-gradient(
    circle at 35% 30%,
    #475569 0%,
    #1e293b 65%,
    #0f172a 100%
  );
  border: 1.5px solid #94a3b8;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
}

.chip-color-green {
  background: radial-gradient(
    circle at 35% 30%,
    #34d399 0%,
    #059669 60%,
    #064e3b 100%
  );
  border: 1.5px solid #a7f3d0;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(5, 150, 105, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

.chip-color-neutral {
  background: radial-gradient(
    circle at 35% 30%,
    #818cf8 0%,
    #4f46e5 60%,
    #312e81 100%
  );
  border: 1.5px solid #c7d2fe;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.25);
}
</style>
